import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Settings,
  LogOut,
  Search,
  Filter,
  Eye,
  Trash2,
  Clock,
  CheckCircle2,
  AlertCircle,
  Save,
  RefreshCw,
  Globe,
  MessageSquare,
  MapPin,
  Link2,
  Monitor,
  Tag,
  Calendar
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { Toaster } from '../ui/sonner';
import { authAPI, auditAPI } from '../../services/api';
import { PixZentLogo } from '../landing/Header';

const statusColors = {
  'New': { bg: 'bg-blue-100', text: 'text-blue-700', icon: AlertCircle },
  'In Progress': { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  'Completed': { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle2 }
};

const sourceColors = {
  'Website': { bg: 'bg-violet-100', text: 'text-violet-700', icon: Globe },
  'Chatbot': { bg: 'bg-pink-100', text: 'text-pink-700', icon: MessageSquare }
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, in_progress: 0, completed: 0, by_source: { website: 0, chatbot: 0 } });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingNotes, setEditingNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, requests, settings

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('pixzent_admin_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      await authAPI.verify();
      setIsAuthenticated(true);
      await loadData();
    } catch (error) {
      console.error('Auth verification failed:', error);
      localStorage.removeItem('pixzent_admin_token');
      localStorage.removeItem('pixzent_admin_auth');
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [submissionsData, statsData] = await Promise.all([
        auditAPI.getAllSubmissions(),
        auditAPI.getStats()
      ]);
      setSubmissions(submissionsData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('pixzent_admin_token');
    localStorage.removeItem('pixzent_admin_auth');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const filteredSubmissions = submissions.filter(sub => {
    const matchesSearch = 
      sub.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.website_url?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || sub.source === sourceFilter;
    return matchesSearch && matchesStatus && matchesSource;
  });

  const handleViewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setEditingNotes(submission.notes || '');
    setIsViewModalOpen(true);
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await auditAPI.updateSubmission(id, { status: newStatus });
      setSubmissions(prev => prev.map(sub => 
        sub.id === id ? { ...sub, status: newStatus } : sub
      ));
      if (selectedSubmission?.id === id) {
        setSelectedSubmission(prev => ({ ...prev, status: newStatus }));
      }
      toast.success(`Status updated to ${newStatus}`);
      loadData();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedSubmission) return;
    setIsSavingNotes(true);
    try {
      await auditAPI.updateSubmission(selectedSubmission.id, { notes: editingNotes });
      setSubmissions(prev => prev.map(sub => 
        sub.id === selectedSubmission.id ? { ...sub, notes: editingNotes } : sub
      ));
      setSelectedSubmission(prev => ({ ...prev, notes: editingNotes }));
      toast.success('Notes saved successfully');
    } catch (error) {
      console.error('Failed to save notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleMarkAuditSent = async () => {
    if (!selectedSubmission) return;
    try {
      const now = new Date().toISOString();
      await auditAPI.updateSubmission(selectedSubmission.id, { 
        audit_sent: true, 
        audit_sent_date: now,
        status: 'Completed'
      });
      setSubmissions(prev => prev.map(sub => 
        sub.id === selectedSubmission.id 
          ? { ...sub, audit_sent: true, audit_sent_date: now, status: 'Completed' } 
          : sub
      ));
      setSelectedSubmission(prev => ({ 
        ...prev, 
        audit_sent: true, 
        audit_sent_date: now, 
        status: 'Completed' 
      }));
      toast.success('Marked as audit sent');
      loadData();
    } catch (error) {
      console.error('Failed to mark audit sent:', error);
      toast.error('Failed to mark audit sent');
    }
  };

  const handleDeleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    try {
      await auditAPI.deleteSubmission(id);
      setSubmissions(prev => prev.filter(sub => sub.id !== id));
      setIsViewModalOpen(false);
      toast.success('Submission deleted');
      loadData();
    } catch (error) {
      console.error('Failed to delete submission:', error);
      toast.error('Failed to delete submission');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-violet-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      
      {/* Meta tags for noindex */}
      <meta name="robots" content="noindex, nofollow" />
      
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 text-white p-6">
        <div className="flex items-center mb-10">
          <PixZentLogo className="h-10 w-auto" />
        </div>

        <nav className="space-y-2">
          <button 
            onClick={() => setActiveTab('dashboard')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              activeTab === 'dashboard' 
                ? 'bg-violet-600/20 text-violet-300' 
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </button>
          <button 
            onClick={() => setActiveTab('requests')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              activeTab === 'requests' 
                ? 'bg-violet-600/20 text-violet-300' 
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <FileText className="w-5 h-5" />
            <span>Audit Requests</span>
          </button>
          <button 
            onClick={() => setActiveTab('settings')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              activeTab === 'settings' 
                ? 'bg-violet-600/20 text-violet-300' 
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </button>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-xl transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <>
            {/* Header */}
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-slate-900 mb-2">Dashboard</h1>
                <p className="text-slate-500">Overview of all audit submissions</p>
              </div>
              <Button onClick={loadData} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-6 gap-4 mb-8">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <p className="text-slate-500 text-sm mb-1">Total</p>
                <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-violet-500">
                  {stats.total}
                </p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <p className="text-slate-500 text-sm mb-1">New</p>
                <p className="text-3xl font-bold text-blue-600">{stats.new}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <p className="text-slate-500 text-sm mb-1">In Progress</p>
                <p className="text-3xl font-bold text-amber-600">{stats.in_progress}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <p className="text-slate-500 text-sm mb-1">Completed</p>
                <p className="text-3xl font-bold text-emerald-600">{stats.completed}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <p className="text-slate-500 text-sm mb-1 flex items-center gap-1"><Globe className="w-3 h-3" /> Website</p>
                <p className="text-3xl font-bold text-violet-600">{stats.by_source?.website || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <p className="text-slate-500 text-sm mb-1 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Chatbot</p>
                <p className="text-3xl font-bold text-pink-600">{stats.by_source?.chatbot || 0}</p>
              </div>
            </div>

            {/* Recent Submissions */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex justify-between items-center">
                <h2 className="font-semibold text-slate-900">Recent Submissions</h2>
                <Button variant="ghost" size="sm" onClick={() => setActiveTab('requests')}>
                  View All
                </Button>
              </div>
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Contact</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Source</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Date</th>
                    <th className="text-right px-4 py-3 text-sm font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {submissions.slice(0, 5).map((submission) => {
                    const StatusIcon = statusColors[submission.status]?.icon || AlertCircle;
                    const SourceIcon = sourceColors[submission.source]?.icon || Globe;
                    return (
                      <tr key={submission.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{submission.full_name}</p>
                            <p className="text-xs text-slate-500">{submission.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${sourceColors[submission.source]?.bg || 'bg-slate-100'} ${sourceColors[submission.source]?.text || 'text-slate-700'}`}>
                            <SourceIcon className="w-3 h-3" />
                            {submission.source}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColors[submission.status]?.bg} ${statusColors[submission.status]?.text}`}>
                            <StatusIcon className="w-3 h-3" />
                            {submission.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-slate-500 text-xs">{formatDate(submission.created_at)}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewSubmission(submission)}
                            className="h-8 px-3 text-xs"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {submissions.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-slate-500">No submissions yet</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Audit Requests Tab */}
        {activeTab === 'requests' && (
          <>
            {/* Header */}
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-slate-900 mb-2">Audit Requests</h1>
                <p className="text-slate-500">Manage and review all free audit form submissions</p>
              </div>
              <Button onClick={loadData} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    placeholder="Search by name, email, website..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-12 rounded-xl border-slate-200"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40 h-12 rounded-xl border-slate-200">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="New">New</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="w-40 h-12 rounded-xl border-slate-200">
                    <Globe className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="Website">Website</SelectItem>
                    <SelectItem value="Chatbot">Chatbot</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Contact</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Website</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Industry</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Source</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Date</th>
                    <th className="text-right px-4 py-3 text-sm font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredSubmissions.map((submission) => {
                    const StatusIcon = statusColors[submission.status]?.icon || AlertCircle;
                    const SourceIcon = sourceColors[submission.source]?.icon || Globe;
                    return (
                      <tr key={submission.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{submission.full_name}</p>
                            <p className="text-xs text-slate-500">{submission.email}</p>
                            <p className="text-xs text-slate-400">{submission.phone}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-slate-700 truncate max-w-[150px]">{submission.website_url}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-600">{submission.industry}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${sourceColors[submission.source]?.bg || 'bg-slate-100'} ${sourceColors[submission.source]?.text || 'text-slate-700'}`}>
                            <SourceIcon className="w-3 h-3" />
                            {submission.source}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColors[submission.status]?.bg} ${statusColors[submission.status]?.text}`}>
                            <StatusIcon className="w-3 h-3" />
                            {submission.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-slate-500 text-xs">{formatDate(submission.created_at)}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewSubmission(submission)}
                            className="h-8 px-3 text-xs"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {filteredSubmissions.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-slate-500">No submissions found</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <>
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Settings</h1>
              <p className="text-slate-500">Manage your admin preferences</p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Account Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-3 border-b border-slate-100">
                      <span className="text-slate-600">Email</span>
                      <span className="text-slate-900 font-medium">dmb@pixzent.com</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-slate-100">
                      <span className="text-slate-600">Role</span>
                      <span className="text-slate-900 font-medium">Administrator</span>
                    </div>
                    <div className="flex justify-between items-center py-3">
                      <span className="text-slate-600">Last Login</span>
                      <span className="text-slate-900 font-medium">{new Date().toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Notification Settings</h3>
                  <p className="text-slate-500 text-sm">
                    Email notifications coming soon. You&apos;ll be able to receive alerts when new audit requests come in.
                  </p>
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Links</h3>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => window.open('https://zcal.co/dibum/30min', '_blank')}>
                      <Calendar className="w-4 h-4 mr-2" />
                      Calendar Settings
                    </Button>
                    <Button variant="outline" onClick={() => window.open('/', '_blank')}>
                      <Globe className="w-4 h-4 mr-2" />
                      View Website
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      {/* View/Edit Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Request Details</DialogTitle>
            <DialogDescription>
              Review and manage this audit request submission.
            </DialogDescription>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-6 mt-4">
              {/* Status & Source Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <label className="text-xs font-medium text-slate-500">Status</label>
                    <Select
                      value={selectedSubmission.status}
                      onValueChange={(value) => handleUpdateStatus(selectedSubmission.id, value)}
                    >
                      <SelectTrigger className="w-36 mt-1 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="New">New</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Source</label>
                    <p className={`mt-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${sourceColors[selectedSubmission.source]?.bg || 'bg-slate-100'} ${sourceColors[selectedSubmission.source]?.text || 'text-slate-700'}`}>
                      {selectedSubmission.source === 'Chatbot' ? <MessageSquare className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                      {selectedSubmission.source}
                    </p>
                  </div>
                </div>
                {!selectedSubmission.audit_sent ? (
                  <Button onClick={handleMarkAuditSent} className="bg-emerald-500 hover:bg-emerald-600 h-9">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Mark Audit Sent
                  </Button>
                ) : (
                  <div className="text-right">
                    <p className="text-sm text-emerald-600 font-medium">✓ Audit Sent</p>
                    <p className="text-xs text-slate-500">{formatDate(selectedSubmission.audit_sent_date)}</p>
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Contact Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-slate-500">Full Name</label>
                    <p className="text-slate-900">{selectedSubmission.full_name}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Email</label>
                    <p className="text-slate-900">
                      <a href={`mailto:${selectedSubmission.email}`} className="text-violet-600 hover:underline">
                        {selectedSubmission.email}
                      </a>
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Phone</label>
                    <p className="text-slate-900">
                      <a href={`tel:${selectedSubmission.phone}`} className="text-violet-600 hover:underline">
                        {selectedSubmission.phone || 'N/A'}
                      </a>
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Website</label>
                    <p>
                      {selectedSubmission.website_url !== 'Via Chatbot' && selectedSubmission.website_url !== 'Not specified' ? (
                        <a href={selectedSubmission.website_url.startsWith('http') ? selectedSubmission.website_url : `https://${selectedSubmission.website_url}`} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">
                          {selectedSubmission.website_url}
                        </a>
                      ) : (
                        <span className="text-slate-500">{selectedSubmission.website_url}</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Industry</label>
                    <p className="text-slate-900">{selectedSubmission.industry}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Location</label>
                    <p className="text-slate-900">{selectedSubmission.location || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {/* Challenge */}
              {selectedSubmission.challenge && selectedSubmission.challenge !== 'Audit request' && (
                <div>
                  <label className="text-xs font-medium text-slate-500">Challenge / Notes from User</label>
                  <p className="mt-1 text-slate-900 bg-slate-50 p-3 rounded-xl text-sm">
                    {selectedSubmission.challenge}
                  </p>
                </div>
              )}

              {/* Tracking Info */}
              <div className="bg-violet-50 rounded-xl p-4">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Monitor className="w-4 h-4" />
                  Tracking Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-xs font-medium text-slate-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> IP Address
                    </label>
                    <p className="text-slate-700">{selectedSubmission.ip_address || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 flex items-center gap-1">
                      <Link2 className="w-3 h-3" /> Referrer
                    </label>
                    <p className="text-slate-700 truncate">{selectedSubmission.referrer || 'Direct'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 flex items-center gap-1">
                      <Globe className="w-3 h-3" /> Page URL
                    </label>
                    <p className="text-slate-700 truncate">{selectedSubmission.page_url || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 flex items-center gap-1">
                      <Monitor className="w-3 h-3" /> User Agent
                    </label>
                    <p className="text-slate-700 truncate text-xs">{selectedSubmission.user_agent || 'N/A'}</p>
                  </div>
                </div>
                
                {/* UTM Parameters */}
                {(selectedSubmission.utm_source || selectedSubmission.utm_medium || selectedSubmission.utm_campaign) && (
                  <div className="mt-4 pt-4 border-t border-violet-200">
                    <h5 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                      <Tag className="w-3 h-3" /> UTM Parameters
                    </h5>
                    <div className="grid grid-cols-5 gap-2 text-xs">
                      <div>
                        <label className="text-slate-500">Source</label>
                        <p className="text-slate-700">{selectedSubmission.utm_source || '-'}</p>
                      </div>
                      <div>
                        <label className="text-slate-500">Medium</label>
                        <p className="text-slate-700">{selectedSubmission.utm_medium || '-'}</p>
                      </div>
                      <div>
                        <label className="text-slate-500">Campaign</label>
                        <p className="text-slate-700">{selectedSubmission.utm_campaign || '-'}</p>
                      </div>
                      <div>
                        <label className="text-slate-500">Term</label>
                        <p className="text-slate-700">{selectedSubmission.utm_term || '-'}</p>
                      </div>
                      <div>
                        <label className="text-slate-500">Content</label>
                        <p className="text-slate-700">{selectedSubmission.utm_content || '-'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Dates */}
              <div className="flex items-center gap-6 text-sm">
                <div>
                  <label className="text-xs font-medium text-slate-500">Created</label>
                  <p className="text-slate-700">{formatDate(selectedSubmission.created_at)}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Last Updated</label>
                  <p className="text-slate-700">{formatDate(selectedSubmission.updated_at)}</p>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-medium text-slate-500">Internal Notes</label>
                <Textarea
                  value={editingNotes}
                  onChange={(e) => setEditingNotes(e.target.value)}
                  placeholder="Add notes about this submission..."
                  className="mt-1 min-h-[80px]"
                />
                <Button
                  onClick={handleSaveNotes}
                  size="sm"
                  className="mt-2"
                  disabled={isSavingNotes}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSavingNotes ? 'Saving...' : 'Save Notes'}
                </Button>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => handleDeleteSubmission(selectedSubmission.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <Button onClick={() => setIsViewModalOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
